<template>
  <div class="mix-control">
    <div class="mix-control-start-stop">
      <button @click="rewind" class="btn-outline btn-sq">
        <backward-icon />
      </button>
      <button :disabled="isDecoding" @click="toggle" class="btn-outline btn-sq">
        <clock-icon v-if="isDecoding" />
        <pause-icon v-else-if="isPlaying" />
        <play-icon v-else />
      </button>
    </div>
    <div class="mix-control-vr"></div>
    <div class="mix-control-clock">{{ clock }}</div>
    <div class="mix-control-tempo">
      BPM
      <input
        class="input"
        type="number"
        :value="mix.tempo"
        @input="onTempoChange"
      />
    </div>
    <div class="mix-control-timeline btn-group">
      <button
        @click="$emit('timeline-type', 'time')"
        class="btn-outline"
        :class="{ active: timelineType === 'time' }"
      >
        Time
      </button>
      <button
        @click="$emit('timeline-type', 'bar')"
        class="btn-outline"
        :class="{ active: timelineType === 'bar' }"
      >
        Bar
      </button>
    </div>
    <div class="mix-control-vr"></div>
    <div class="mix-control-name">
      {{ project.name }} Mix v{{ mix.version }}
      <input
        class="input"
        type="text"
        placeholder="Description"
        :value="mix.description"
        @input="onDescriptionChange"
      />
    </div>
    <div class="mix-control-vr"></div>
    <div ref="trackPicker" class="mix-control-tracks">
      <transition name="fade">
        <div v-if="showTrackPicker" class="mix-control-tracks-add">
          <track-picker @add="onAddTrack" @add-all="onAddAllTracks" />
        </div>
      </transition>
      <a
        href="#"
        @click.prevent.stop="showTrackPicker = true"
        class="mix-control-tracks-btn"
        ><plus-icon /> Add Tracks</a
      >
    </div>
    <div class="mix-control-space"></div>
    <div
      v-if="mixPropsDirty"
      title="Mix has unsaved properties"
      class="mix-control-unsaved"
    >
      <exclamation-triangle-icon />
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import {
  PlayIcon,
  PauseIcon,
  BackwardIcon,
  ClockIcon,
  PlusIcon,
} from "@heroicons/vue/24/solid";
import { ExclamationTriangleIcon } from "@heroicons/vue/24/outline";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { formatTime } from "@/misc";
import { Mix, Project, Track } from "@/models";
import TrackPicker from "./TrackPicker.vue";
import { TimelineType } from "@/views/MixView.vue";

@Options({
  components: {
    PlayIcon,
    PlusIcon,
    PauseIcon,
    BackwardIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    TrackPicker,
  },
  props: {
    timelineType: {
      type: String,
      default: "time",
    },
  },
  computed: {
    ...mapState("mixes", [
      "isPlaying",
      "isDecoding",
      "position",
      "mix",
      "mixPropsDirty",
    ]),
    ...mapState("projects", { project: "current" }),
    ...mapGetters("projects", ["tracksNotInUse"]),
  },
  methods: {
    ...mapActions("mixes", ["toggle", "stop", "play", "patchMix"]),
    ...mapMutations("mixes", ["setPosition"]),
  },
  data() {
    return {
      showTrackPicker: false,
    };
  },
})
export default class MixControl extends Vue {
  isPlaying!: boolean;
  isDecoding!: boolean;
  position!: number;
  mix!: Mix;
  project!: Project;
  tracksNotInUse!: Track[];
  showTrackPicker!: boolean;
  mixPropsDirty!: boolean;
  timelineType!: TimelineType;

  toggle!: () => Promise<void>;
  play!: () => Promise<void>;
  stop!: () => Promise<void>;
  setPosition!: (value: number) => void;
  patchMix!: (payload: Partial<Mix>) => Promise<void>;

  get clock() {
    return formatTime(this.position / 1000);
  }

  async rewind() {
    let wasPlaying = this.isPlaying;
    if (wasPlaying) {
      await this.stop();
    }
    this.setPosition(0);
    this.$emit("rewind");
    if (wasPlaying) {
      await this.play();
    }
  }

  onTempoChange(e: Event) {
    const target = e.target as HTMLInputElement;
    const tempo = target.valueAsNumber;
    this.patchMix({ tempo });
  }

  onDescriptionChange(e: Event) {
    const target = e.target as HTMLInputElement;
    const description = target.value;
    this.patchMix({ description });
  }

  mounted() {
    window.addEventListener("mousedown", this.onMouseDown);
  }

  beforeUnmount() {
    window.removeEventListener("mousedown", this.onMouseDown);
  }

  onMouseDown(e: MouseEvent) {
    if (this.showTrackPicker) {
      let node: HTMLElement | null = e.target as HTMLElement;
      const trackPicker = this.$refs.trackPicker as HTMLElement;
      while (node) {
        if (node === trackPicker) {
          return;
        }
        node = node.parentElement;
      }
      this.showTrackPicker = false;
    }
  }

  onAddTrack(track: Track) {
    this.$store.dispatch("mixes/createMixTrack", track._id);
  }

  onAddAllTracks() {
    this.tracksNotInUse.forEach((track) => {
      this.$store.dispatch("mixes/createMixTrack", track._id);
    });
    this.showTrackPicker = false;
  }
}
</script>

<style lang="scss">
.mix-control {
  height: 46px;
  background: linear-gradient(135deg, #e0e0e0 0%, #c0c0c0 100%);
  box-sizing: border-box;
  border-top: 1px solid #999;
  border-bottom: 1px solid #999;
  padding: 8px;
  display: flex;
  align-items: center;
}

.mix-control-start-stop button.btn-sq:last-child {
  margin-right: 0;
}

.mix-control-clock {
  height: 28px;
  background-color: #777;
  border: 1px solid #444;
  padding: 2px 8px 2px 19px;
  box-sizing: border-box;
  font-size: 1.5em;
  font-weight: bold;
  width: 120px;
  color: #eee;
}

.mix-control-vr {
  margin: 0 12px;
  height: 28px;
  width: 0;
  border-right: 1px solid #999;
  border-left: 1px solid #eee;
}

.mix-control-tempo {
  margin-left: 1em;
  font-weight: bold;
  input.input {
    width: 56px;
  }
}

.mix-control-name {
  font-weight: bold;
  input.input {
    margin-left: 0.5em;
    width: 250px;
  }
}

.mix-control-tracks {
  position: relative;

  .mix-control-tracks-add {
    position: absolute;
    top: 0;
    left: 0;
    width: 270px;
    background: #f0f0f0;
    border: 1px solid #ccc;
    border-radius: 3px;
    padding: 28px 8px 8px;
    z-index: 1;
  }

  a.mix-control-tracks-btn {
    height: 28px;
    display: flex;
    align-items: center;
    margin: 0 8px;
    box-sizing: border-box;
    color: inherit;
    text-decoration: none;
    z-index: 2;
    position: relative;
    svg {
      width: 20px;
      height: 20px;
      margin-right: 0.3em;
    }
  }
}

.mix-control-space {
  flex-grow: 1;
}

.mix-control-unsaved {
  width: 20px;
  height: 20px;
  position: relative;
  top: 1px;
  cursor: help;
}

.mix-control-timeline {
  margin-left: 0.5em;
  .btn-outline {
    font-size: 0.8em;
  }
}
</style>
